import { useEffect, useRef } from 'react';
import {
  isConnectorAvailable,
  onSessionExpired as onConnectorSessionExpired,
  onRemoteAccessRevoked,
} from '../lib/connector';
import { registerSessionDeadSideEffects } from '../lib/sessionDead';
import {
  isRemoteAccessRevocationPayload,
  markServiceSessionDead,
  registerServiceSessionDeadSideEffects,
} from '../lib/serviceSessionDead';

function handleRemoteAccessRevocation(payload, eventName) {
  const raw = payload && typeof payload === 'object' ? payload : {};
  // Fleet session_expired is always service remote-access; enriched payloads feature-detect.
  if (eventName !== 'session_expired' && !isRemoteAccessRevocationPayload(raw)) {
    return;
  }
  const reason = raw.reason || raw.trigger || eventName;
  markServiceSessionDead(String(reason), raw);
}

/**
 * Connector `session_expired` / `remote_access_revoked` → service-session-dead banner.
 * Web JWT may remain valid; user is not signed out of the working session.
 */
export default function SessionExpiredBridge() {
  const unlistenRefs = useRef([]);

  useEffect(() => {
    registerSessionDeadSideEffects();
    registerServiceSessionDeadSideEffects();

    let cancelled = false;
    let pollTimer = null;

    const subscribe = async () => {
      if (cancelled || !isConnectorAvailable()) return;
      try {
        const unsubs = await Promise.all([
          onConnectorSessionExpired((payload) => {
            handleRemoteAccessRevocation(payload, 'session_expired');
          }),
          onRemoteAccessRevoked((payload) => {
            handleRemoteAccessRevocation(payload, 'remote_access_revoked');
          }),
        ]);
        if (cancelled) {
          unsubs.forEach((fn) => { if (typeof fn === 'function') fn(); });
          return;
        }
        unlistenRefs.current = unsubs;
      } catch (err) {
        console.error('[SessionExpiredBridge] listen failed:', err);
      }
    };

    void subscribe();

    let attempts = 0;
    pollTimer = window.setInterval(() => {
      attempts += 1;
      if (unlistenRefs.current.length > 0 || attempts > 15) {
        window.clearInterval(pollTimer);
        return;
      }
      void subscribe();
    }, 2000);

    return () => {
      cancelled = true;
      if (pollTimer) window.clearInterval(pollTimer);
      unlistenRefs.current.forEach((fn) => {
        if (typeof fn === 'function') fn();
      });
      unlistenRefs.current = [];
    };
  }, []);

  return null;
}
